// =====================================================
// Swiss Finance OS - Receipt List Component
// Display list of receipts for a transaction
// =====================================================

import React, { useState, useEffect } from 'react';
import { receiptService, type ReceiptUpload } from '@/services/receiptService';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  FileText,
  Image as ImageIcon,
  Loader2,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ReceiptListProps {
  transactionId: string;
  onReceiptClick: (receipt: ReceiptUpload) => void;
  className?: string;
}

export const ReceiptList: React.FC<ReceiptListProps> = ({
  transactionId,
  onReceiptClick,
  className = ''
}) => {
  const [receipts, setReceipts] = useState<ReceiptUpload[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadReceipts();
  }, [transactionId]);

  const loadReceipts = async () => {
    setIsLoading(true);
    try {
      const data = await receiptService.getTransactionReceipts(transactionId);
      setReceipts(data);
    } catch (error) {
      console.error('Error loading receipts:', error);
      toast({
        title: 'Error loading receipts',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType === 'application/pdf') {
      return <FileText className="h-8 w-8 text-red-500" />;
    }
    return <ImageIcon className="h-8 w-8 text-blue-500" />;
  };

  const getOcrStatusBadge = (status: string) => {
    const statusConfig = {
      pending: {
        label: 'Pending',
        className: 'bg-yellow-100 text-yellow-800',
        icon: Clock
      },
      processing: {
        label: 'Processing',
        className: 'bg-blue-100 text-blue-800',
        icon: Loader2
      },
      processed: {
        label: 'Verified',
        className: 'bg-green-100 text-green-800',
        icon: CheckCircle
      },
      failed: {
        label: 'Failed',
        className: 'bg-red-100 text-red-800',
        icon: XCircle
      }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={`${config.className} flex items-center gap-1 text-xs`}>
        <Icon className={`h-3 w-3 ${status === 'processing' ? 'animate-spin' : ''}`} />
        {config.label}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (receipts.length === 0) {
    return (
      <div className="text-center p-8 border border-dashed border-gray-300 rounded-lg bg-gray-50">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
        <p className="text-sm text-gray-600">No receipts uploaded yet</p>
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-brand-primary">
          Receipts ({receipts.length})
        </h4>
        <Button
          variant="ghost"
          size="sm"
          onClick={loadReceipts}
          className="text-xs"
        >
          Refresh
        </Button>
      </div>

      {receipts.map((receipt) => (
        <div
          key={receipt.id}
          onClick={() => onReceiptClick(receipt)}
          className="
            flex items-center gap-3 p-3 border border-gray-200 rounded-lg 
            bg-white hover:bg-gray-50 cursor-pointer transition-colors
            group
          "
        >
          {/* File Icon */}
          <div className="flex-shrink-0">
            {getFileIcon(receipt.mime_type)}
          </div>

          {/* File Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-medium text-gray-900 truncate group-hover:text-brand-primary transition-colors">
                {receipt.file_name}
              </p>
              {receipt.has_mismatches && (
                <AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0" />
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>{(receipt.file_size / 1024).toFixed(1)} KB</span>
              <span>•</span>
              <span>{new Date(receipt.uploaded_at).toLocaleDateString('de-CH')}</span>
              {receipt.ocr_confidence && receipt.ocr_status === 'processed' && (
                <>
                  <span>•</span>
                  <span className="text-green-600 font-medium">
                    {receipt.ocr_confidence.toFixed(0)}% confidence
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Status Badge */}
          <div className="flex-shrink-0">
            {getOcrStatusBadge(receipt.ocr_status)}
          </div>
        </div>
      ))}

      {/* Summary Stats */}
      {receipts.length > 0 && (
        <div className="pt-2 border-t border-gray-200 mt-4">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>
              {receipts.filter(r => r.ocr_status === 'processed').length} verified
            </span>
            {receipts.some(r => r.has_mismatches) && (
              <span className="text-orange-600 font-medium">
                {receipts.filter(r => r.has_mismatches).length} with issues
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
